import { CommonModule, TitleCasePipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DragDropModule } from "primeng/dragdrop";
import { SkeletonModule } from "primeng/skeleton";
import { map } from "rxjs";
import { ToastService } from "../../../../@core/services/toast.service";
import { getRandomColor } from "../../../../common/utils";
import { IDocumentType, ITagType, TAG_TYPES } from "../../../models/settings.model";
import { DocumentTypeFilter, DocumentTypeService } from "../../../services/document-type.service";
import { TagsService } from "../../../services/tags.service";
import { FileSelectionComponent } from "../../../../@shared/components/file-selection/file-selection.component";
import { FetchFunction, FileItem, FileSelectionConfig, FileSelectionSubmitEvent } from "../../../../@shared/components/file-selection/file-selection.model";
import { CheckScreenSize } from "../../../../services/check-screen-size.service";
import { DocumentTypesListComponent } from "../document-types-list/document-types-list.component";
import _ from "lodash";
import { ButtonComponent } from "../../../../@core/components/ui/button/button.component";
import { CheckboxComponent } from "../../../../@core/components/ui/checkbox/checkbox.component";

@Component({
    selector: 'app-document-tags',
    imports: [
        CardModule,
        ButtonModule,
        CommonModule,
        ConfirmDialogModule,
        SkeletonModule,
        FileSelectionComponent,
        DragDropModule,
        DocumentTypesListComponent,
        ButtonComponent,
        CheckboxComponent
    ],
    standalone: true,
    templateUrl: './document-tags.component.html',
    styleUrl: './document-tags.component.scss',
    providers: [ConfirmationService]
})
export class DocumentTagsComponent implements OnInit {
    selectedDocType: IDocumentType = { _id: '', name: '', summaryDescription: '', comparisionDescription: '' } as IDocumentType;
    linkedTagsLoading: boolean = false
    linkedTags: (ITagType & {
        createdByLabel?: string,
        createdByInitials?: string,
        address?: string,
    })[] = []
    isSmallScreen: boolean = false
    TAG_TYPES_ICONS: { [key: string]: string } = {
        [TAG_TYPES.CLIENT]: 'pi pi-user',
        [TAG_TYPES.LOCATION]: 'pi pi-map-marker',
        [TAG_TYPES.SCOPE_OF_WORK]: 'pi pi-bullseye',
        [TAG_TYPES.TYPE_OF_CONTRACT]: 'pi pi-file',
    };
    connectTagsVisible: boolean = false
    linkedTagsMap: { [key: string]: boolean } = {}

    fileSelectionConfig: FileSelectionConfig = {
        title: 'Connect Tag Types',
        submitButtonLabel: 'Connect Selected',
        showSearch: true,
        searchPlaceholder: 'Search tag types',
        selectedPanelTitle: 'Selected Tag Types',
        selectedEmptyMessage: 'Select tag types to add them'
    };

    fetchTagTypes: FetchFunction = (event) => {
        return this.tagService.getTagTypes({
            getLinkedTags: false,
            filters: {
                searchValue: event.searchQuery || '',
                page: event.page + 1,
                limit: event.pageSize,
            },
        }).pipe(
            map(res => {
                const tagTypes = res.data?.tagTypes?.filter(item => item.tagType !== TAG_TYPES.REPO_EXTRACTED) ?? [];
                const items: FileItem[] = tagTypes.map(tag => ({
                    id: tag._id ?? '',
                    name: tag.name,
                    metadata: { originalTag: tag },
                    selected: !!this.linkedTagsMap[tag._id ?? ''],
                }));
                return { items, totalRecords: res.data?.total ?? items.length };
            })
        );
    };
    draggedTagIdx: number | null = null;
    docTypeLoading: boolean = false;
    docTypes: IDocumentType[] = [];

    constructor(
        private documentTypeService: DocumentTypeService,
        private tagService: TagsService,
        private confirmationService: ConfirmationService,
        private toast: ToastService,
        private checkScreenSizeService: CheckScreenSize
    ) {

    }

    ngOnInit() {
        this.isSmallScreen = this.checkScreenSizeService.checkScreenSize();
        this.getDocumentTypes();
    }

    getDocumentTypes() {
        this.docTypeLoading = true;
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL]).subscribe(res => {
            if (res && res.success) {
                this.docTypeLoading = false;
                this.docTypes = res.data;
            } else {
                this.docTypeLoading = false;
            }
        });
    }

    onGetDoctype(docType: IDocumentType) {
        this.selectedDocType = docType
        this.getLinkedTags();
    }

    getLinkedTags() {
        this.linkedTagsLoading = true
        this.documentTypeService.getLinkedTagTypes(this.selectedDocType?._id).subscribe(res => {
            if (res && res.success) {
                this.linkedTagsLoading = false
                this.linkedTags = res.data || []
                this.linkedTagsMap = this.linkedTags.reduce((acc, cur) => { acc[cur._id ?? ''] = true; return acc }, {} as { [key: string]: boolean })
            }
            else
                this.linkedTagsLoading = false
        })
    }

    openConnectDialog() {
        this.fileSelectionConfig = {
            ...this.fileSelectionConfig,
            initialSelectedFiles: this.linkedTags.map(tag => ({
                id: tag._id ?? '',
                name: tag.name,
                metadata: { originalTag: tag },
            })),
        };
        this.connectTagsVisible = !this.connectTagsVisible
    }

    submitDocSelection(event: FileSelectionSubmitEvent) {
        const selectedTagTypes = (event.allSelectedFiles ?? []).map(f => f.metadata?.originalTag as ITagType).filter(Boolean);
        const data = _.cloneDeep(this.selectedDocType)
        this.selectedDocType.linkedTagTypes = selectedTagTypes.map(item => item._id ?? '')
        this.documentTypeService.addUpdateDocType(this.selectedDocType).subscribe((res: any) => {
            if (res && res.success) {
                this.toast.success('Connected Tag Types successfully')
                this.connectTagsVisible = false
                this.linkedTags = selectedTagTypes
                this.linkedTagsMap = this.linkedTags.reduce((acc, cur) => { acc[cur._id ?? ''] = true; return acc }, {} as { [key: string]: boolean })
            }
            else {
                this.toast.error('Failed to connect tag types')
                this.selectedDocType = data
                this.connectTagsVisible = false
            }
        })
    }

    deleteLinkedTag(tag: ITagType) {
        this.confirmationService.confirm({
            header: 'Are you sure?',
            message: `This action will unlink the tag type`,
            accept: () => {
                const data = _.cloneDeep(this.selectedDocType)
                this.selectedDocType.linkedTagTypes = this.selectedDocType?.linkedTagTypes?.filter(item => item != tag._id) as string[] || []
                this.documentTypeService.addUpdateDocType(this.selectedDocType).subscribe(res => {
                    if (res && res.success) {
                        this.toast.success('Unlinked Tag Type successfully')
                        this.linkedTags = this.linkedTags.filter(item => (res.data.linkedTagTypes as string[]).includes(item._id ?? ''))
                        this.linkedTagsMap = this.linkedTags.reduce((acc, cur) => { acc[cur._id ?? ''] = true; return acc }, {} as { [key: string]: boolean })
                    }
                    else {
                        this.toast.error('Failed to Unlink tag type')
                        this.selectedDocType = data
                    }
                })
            },
            reject: () => { },
        });
    }

    cancel() {
        this.connectTagsVisible = !this.connectTagsVisible
    }

    getRandomColor(): string {
        return getRandomColor()
    }

    getSkeletonCount(count: number) {
        return Array(count).fill(0);
    }

    dragStart(index: number) {
        this.draggedTagIdx = index;
    }

    dropTag(dropIdx: number) {
        if (this.draggedTagIdx !== null && this.draggedTagIdx !== dropIdx) {
            const draggedTag = this.linkedTags[this.draggedTagIdx];
            this.linkedTags.splice(this.draggedTagIdx, 1);
            this.linkedTags.splice(dropIdx, 0, draggedTag);
            this.draggedTagIdx = null;
            this.updateTagsOrder();
        }
    }

    updateTagsOrder = _.debounce(() => {
        const docType: IDocumentType = {
            ...this.selectedDocType,
            linkedTagTypes: this.linkedTags
        };
        this.documentTypeService.addUpdateDocType(docType).subscribe(res => {
            if (!res || !res.success) {
                this.toast.error('Error modifiying the tag types order');
                return;
            }
            else {
                this.toast.success("Sequence updated successfully")
                this.linkedTagsMap = this.linkedTags.reduce((acc, cur) => { acc[cur._id ?? ''] = true; return acc }, {} as { [key: string]: boolean })
            }
        })
    }, 1000)

    handleConfirmAccept(message: any) {
        // Call the accept callback if it exists
        if (message.accept) {
            message.accept();
        }
        // Hide the dialog
        this.confirmationService.close();
    }

    handleConfirmReject(message: any) {
        // Call the reject callback if it exists
        if (message.reject) {
            message.reject();
        }
        // Hide the dialog
        this.confirmationService.close();
    }
}

